<script setup lang="ts">
import type { UserSession } from '#auth-utils';
import type { ResultTuple } from '~~/core/helpers/result';
import type { PhoneValidationReturn } from '~~/core/ports/repositories/AuthRepository';

import { useEvents } from '~/composables/events/useEvents';
import { useIdentityTracking } from '~/composables/events/useIdentityTracking';
import { useAuthCrossTabSync } from '~/composables/useAuthCrossTabSync';
import { useLoginModal } from '~/composables/useLoginModal';
import useSocialLogin from '~/composables/useSocialLogin/useSocialLogin';
import {
	MixpanelProps,
	MixpanelUserActionValues
} from '~/core/service/mixpanel';
import { nuxtAuthRepository } from '~~/adapters/repositories/nuxt/nuxtAuthRepository';
import { isEmpty } from '~~/utils';

import CheckIcon from '~icons/material-symbols-light/check';
import CheckCircleIcon from '~icons/material-symbols-light/check-circle';
import Westicon from '~icons/material-symbols-light/west';
import ErrorCircleIcon from '~icons/material-symbols/error';

import messages from './LoginModal.locale.json';

// Lazy load LoginForm para melhorar INP - o modal abre instantaneamente
const LoginForm = defineAsyncComponent({
	loader: () => import('@/components/LoginForm/Form.vue'),
	delay: 0
});

const { trackSignAndLogin, trackingCloseClicked, trackingGoToPrevious } =
	useEvents();
const { stitchUserSessions } = useIdentityTracking();

// TODO: adicionar testes para todos os fluxos desse componente

const { handleSubmit } = useSocialLogin();
const { user, fetch: fetchSession, session } = useUserSession();
const { modalState, closeLoginModal } = useLoginModal();
const { notifyLogin } = useAuthCrossTabSync();

const isSuccess = shallowRef<boolean>(false);
const hasErrorOnCompleteRegistrationStep = shallowRef<boolean>(false);
const isOTPValid = shallowRef<boolean | null>(null);
const formRef = ref<typeof LoginForm | null>(null);

const strings = messages.pt;

function validateAndStichSessions() {
	if (!user.value) return;
	stitchUserSessions({
		userId: user.value.id || user.value.email || '',
		userName: user.value.name || '',
		userEmail: user.value.email || '',
		userPhone: user.value.phoneNumber || '',
		loginMethod: formRef.value?.getMethodValidation
	});

	// Notify other tabs that user has logged in
	notifyLogin();
}

async function handleOTPValidation(
	result: ResultTuple<PhoneValidationReturn> | null
) {
	const [data, error] = result || [];

	// TODO: adicionar tratamento de erro
	if (error) {
		isOTPValid.value = false;
		return;
	}

	await fetchSession();

	if (user.value?.hasCompletedRegistration) {
		validateAndStichSessions();

		closeModal();
		return;
	}

	if (!data?.accessToken) {
		isOTPValid.value = false;
		return;
	}

	isOTPValid.value = true;

	if (formRef.value?.isFourthStep) {
		isSuccess.value = true;
		return;
	}

	formRef.value?.goToFourthStep();
}

function isUserLoggedIn(session: UserSession | null): boolean {
	if (!session) return false;

	const { id, ...sessionData } = session || {};

	return !isEmpty(sessionData);
}

const handleSocialLogin = handleSubmit(async () => {
	await fetchSession();

	if (user.value?.hasCompletedRegistration) {
		validateAndStichSessions();

		closeModal();
		return;
	}

	/**
	 ** Essa verificação serve para evitar que o usuário avance para a próxima
	 ** etapa após fechar a janela de login social sem completar o login de fato
	 */
	if (isUserLoggedIn(session.value)) return formRef.value?.goToFourthStep();
});

const OTPLogin = useAsyncState(
	async (phone: string) =>
		nuxtAuthRepository.phone({
			phoneNumber: phone
		}),
	null,
	{
		immediate: false,
		onError: () => {
			// TODO: Em caso de erro, mostraremos um toast?
			// TODO: Adicionar track de erro
			formRef.value?.goToSecondStep();
		}
	}
);

const OTPValidation = useAsyncState(
	async ({ phone, otp }: { phone: string; otp: string }) =>
		nuxtAuthRepository.getCreatePhoneUser({
			phoneNumber: phone,
			otp
		}),
	null,
	{
		immediate: false,
		onError: error => {
			console.error('error', error);
			// TODO: Em caso de erro, mostraremos um toast?
			// TODO: Adicionar track de erro
		},
		onSuccess: async result => handleOTPValidation(result)
	}
);

const socialOTPValidation = useAsyncState(
	async (otp: string) => nuxtAuthRepository.verifySocialRegistration({ otp }),
	null,
	{
		immediate: false,
		onError: error => {
			console.error('error', error);
			// TODO: Em caso de erro, mostraremos um toast?
			// TODO: Adicionar track de erro
		},
		onSuccess: async result => handleOTPValidation(result)
	}
);

const completeSMSRegistration = useAsyncState(
	async ({ email, name }: { name: string; email?: string }) =>
		nuxtAuthRepository.completePhoneRegistration({
			email,
			name
		}),
	[{ accessToken: '', hasCompletedRegistration: false, tokenType: '' }, null],
	{
		immediate: false,
		onError: error => {
			console.error('error', error);
			// TODO: Em caso de erro, mostraremos um toast?
			// TODO: Adicionar track de erro
		},
		async onSuccess([_data, error]: ResultTuple<PhoneValidationReturn>) {
			if (error) {
				hasErrorOnCompleteRegistrationStep.value = true;
				return;
			}

			await fetchSession();

			validateAndStichSessions();

			isSuccess.value = true;
		}
	}
);

const completeSocialRegistration = useAsyncState(
	async (phoneNumber: string) =>
		nuxtAuthRepository.completeSocialRegistration({
			phoneNumber
		}),
	null,
	{
		immediate: false,
		onError: error => {
			console.error('error', error);
			// TODO: Em caso de erro, mostraremos um toast?
			// TODO: Adicionar track de erro
		},
		onSuccess: async () => {
			await fetchSession();

			validateAndStichSessions();
		}
	}
);

function retryCompleteRegistration() {
	hasErrorOnCompleteRegistrationStep.value = false;
	formRef.value?.goToFourthStep();
}

const isLoading = computed<boolean>(
	() =>
		OTPValidation.isLoading.value ||
		socialOTPValidation.isLoading.value ||
		completeSMSRegistration.isLoading.value ||
		completeSocialRegistration.isLoading.value
);

const footerClasses = computed<string>(() => {
	if (isSuccess.value) {
		return 'w-full border-t border-white-dark-2 px-4 md:px-5 py-4';
	}

	if (formRef?.value?.isThirdStep) {
		return 'lg:py-4 items-center bg-white border-t border-white-dark-3 px-4 py-4 md:py-2 flex w-full md:justify-end justify-center';
	}

	return 'justify-end border-none px-7 pb-4 pt-0!';
});

const title = computed<string>(() => {
	if (isSuccess.value) return strings.successModal.title;

	if (modalState.value?.context === 'favorites' && formRef.value?.isFirstStep) {
		return strings.favoritesModal.title;
	}

	return formRef.value?.title || '';
});

const customDescription = computed<string>(() => {
	if (modalState.value.context === 'favorites') {
		return strings.favoritesModal.description;
	}
	return '';
});

function closeModal() {
	closeLoginModal();
	isSuccess.value = false;
	isOTPValid.value = null;
}

const route = useRoute();

watch(
	() => modalState.value.isOpen,
	newValue => {
		if (!newValue) {
			addEventsCloseModal();
		}
	}
);

watch(isSuccess, newValue => {
	if (newValue) {
		addEventsSignupCompleted();
	}
});

const currentStepMapper: Record<number, string> = {
	1: 'sign_in_up',
	2: 'phone_confirmation',
	3: '2fa_authentication',
	4: 'sign_up_completed'
};

const getCurrentStep = (step: number) => {
	return currentStepMapper[step] || 'sign_in_up';
};

const addEventsCloseModal = () => {
	const step = formRef.value?.getCurrentStep || 1;
	trackingCloseClicked({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.CURRENT_FLOW]: MixpanelUserActionValues.SIGN_IN_UP,
		[MixpanelProps.CURRENT_STEP]: getCurrentStep(step)
	});
};

const addEventsGoToPrevious = () => {
	const step = formRef.value?.getCurrentStep + 1 || 2;
	trackingGoToPrevious({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.CURRENT_FLOW]: MixpanelUserActionValues.SIGN_IN_UP,
		[MixpanelProps.CURRENT_STEP]: getCurrentStep(step)
	});
};

const addEventsSignupCompleted = () => {
	const loginType = formRef.value?.getMethodValidation || '';
	trackSignAndLogin.trackingSignupCompleted({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.USER_NAME]: user.value?.name || '',
		[MixpanelProps.USER_EMAIL]: user.value?.email || '',
		[MixpanelProps.USER_PHONE]: user.value?.phoneNumber || '',
		[MixpanelProps.LOGIN_TYPE]: loginType
	});
};

const goToPreviousStep = () => {
	addEventsGoToPrevious();
	formRef.value?.goToPreviousStep();
};

const addEventsTermsOrPolicy = (type: string) => {
	const isTerms = type === 'terms';
	const isPolicy = type === 'policy';
	trackSignAndLogin.trackingTermsOrpolicy({
		[MixpanelProps.USER_NAME]: user.value?.name || '',
		[MixpanelProps.USER_EMAIL]: user.value?.email || '',
		[MixpanelProps.USER_PHONE]: user.value?.phoneNumber || '',
		[MixpanelProps.CLICK_LOGIN]: true,
		[MixpanelProps.TERMS_OF_USE]: isTerms,
		[MixpanelProps.PRIVACY_POLICY]: isPolicy
	});
};
</script>

<template>
	<ModalPortalModal
		:open="modalState.isOpen"
		:title="title"
		:show-close-icon="formRef?.isFirstStep"
		title-class="text-xl md:text-2xl"
		header-class="flex items-center h-[4.5rem] md:h-[4.75rem]"
		content-class="z-[101] w-[343px] md:w-[420px] bg-white overflow-visible"
		:footer-class="footerClasses"
		@update:open="
			open => {
				if (!open) closeLoginModal();
			}
		"
	>
		<template #trigger>
			<div class="w-full">
				<slot></slot>
			</div>
		</template>

		<template #header>
			<DialogTitle
				class="flex items-center gap-4 font-matter-sq text-xl font-normal leading-normal tracking-[-0.03rem] text-primary md:gap-6 md:text-2xl"
			>
				<Westicon
					v-show="
						formRef?.isSecondStep ||
						formRef?.isThirdStep ||
						formRef?.isShowingOtpForm
					"
					class="cursor-pointer"
					@click="goToPreviousStep"
				/>
				{{ title }}
			</DialogTitle>
			<DialogDescription />
		</template>

		<template #default>
			<Suspense>
				<template #fallback>
					<!-- Skeleton enquanto LoginForm carrega - melhora INP -->
					<div class="flex flex-col gap-4 px-7 py-5 lg:py-6">
						<div class="h-5 w-3/4 animate-pulse rounded bg-gray-200" />
						<div class="h-12 w-full animate-pulse rounded bg-gray-200" />
						<div class="h-12 w-full animate-pulse rounded bg-gray-200" />
						<div class="h-10 w-full animate-pulse rounded bg-gray-200" />
					</div>
				</template>
				<LoginForm
					v-show="!isSuccess && !hasErrorOnCompleteRegistrationStep"
					ref="formRef"
					v-model:is-o-t-p-valid="isOTPValid"
					class="px-7 py-5 lg:py-6"
					:is-loading="isLoading"
					:custom-description="customDescription"
					@social-login="handleSocialLogin"
					@otp-login="$event => OTPLogin.execute(0, $event)"
					@validation-code="$event => OTPValidation.execute(0, $event)"
					@social-validation-code="
						$event => socialOTPValidation.execute(0, $event)
					"
					@complete-phone-registration="
						$event => completeSMSRegistration.execute(0, $event)
					"
					@complete-social-registration="
						$event => completeSocialRegistration.execute(0, $event)
					"
				/>
			</Suspense>

			<div
				v-show="isSuccess && !isLoading"
				class="flex flex-col items-center justify-center gap-3 px-4 pb-5 pt-6 md:px-5 md:pb-8 md:pt-7"
			>
				<div
					class="flex size-15 items-center justify-center rounded-full bg-success-light-1 md:mb-1"
				>
					<CheckCircleIcon class="size-13 text-success" />
				</div>
				<span
					class="text-center font-matter-sq text-lg font-normal tracking-[-0.0225rem] text-primary md:text-xl md:tracking-[-0.025rem]"
				>
					{{ strings.successModal.message }}
				</span>

				<p
					class="text-center font-sans text-sm font-normal text-primary md:text-base"
				>
					{{ strings.successModal.description }}
				</p>
			</div>

			<div
				v-show="hasErrorOnCompleteRegistrationStep && !isLoading"
				class="flex flex-col items-center justify-center gap-3 px-4 pb-5 pt-6 md:px-5 md:pb-8 md:pt-7"
			>
				<div
					class="flex size-15 items-center justify-center rounded-full bg-error-light-1 md:mb-1"
				>
					<ErrorCircleIcon class="size-13 text-error" />
				</div>
				<span
					class="text-center font-matter-sq text-lg font-normal tracking-[-0.0225rem] text-primary md:text-xl md:tracking-[-0.025rem]"
				>
					{{ strings.completeRegistrationErrorModal.message }}
				</span>

				<p
					class="text-center font-sans text-sm font-normal text-primary md:text-base"
				>
					{{ strings.completeRegistrationErrorModal.description }}
				</p>
			</div>
		</template>

		<template v-if="isSuccess || hasErrorOnCompleteRegistrationStep" #footer>
			<Button v-if="isSuccess" class="h-12" @click="closeModal">
				<CheckIcon class="size-5" />

				{{ strings.successModal.confirmButton }}
			</Button>

			<Button
				v-if="hasErrorOnCompleteRegistrationStep"
				class="h-12"
				@click="retryCompleteRegistration"
			>
				{{ strings.completeRegistrationErrorModal.retryButton }}
			</Button>
		</template>

		<template v-else #footer>
			<div v-if="formRef?.isShowingOtpForm" class="w-full">
				<Button
					:disabled="isLoading"
					class="h-12 w-full text-base font-medium"
					data-testid="submit-confirmation-button"
					@click="formRef?.submitOtpCode"
				>
					<IconLineMdLoadingTwotoneLoop
						v-if="isLoading"
						class="size-[1.7rem]"
					/>

					<template v-else>
						{{ strings.otpStep.confirmButton }}
					</template>
				</Button>
			</div>

			<p
				v-else
				class="flex cursor-default flex-wrap justify-center gap-[6px] pb-1 text-center text-xs leading-normal text-primary-light-1"
			>
				Prosseguindo, você aceita os
				<NuxtLink
					to="/termos-de-uso"
					class="underline decoration-primary-light-1 underline-offset-[6px]"
					target="_blank"
					@click="addEventsTermsOrPolicy('terms')"
				>
					Termos de Uso
				</NuxtLink>
				e reconhece a
				<NuxtLink
					to="/politica-de-privacidade"
					class="underline decoration-primary-light-1 underline-offset-[6px]"
					target="_blank"
					@click="addEventsTermsOrPolicy('policy')"
				>
					Política de Privacidade
				</NuxtLink>
			</p>
		</template>
	</ModalPortalModal>
</template>
